import React, { useEffect, useState } from "react";
import {
  getAllTemplates,
  saveTemplate,
  deleteTemplate,
} from "../../Services/AllotmentLetterTemplate";

const EMPTY_TEMPLATE = {
  name: "",
  project_id: "",
  wing_id: "",
  show_condition: "always",
  subject: "",
  section1_paragraph: "",
  has_parking_section: false,
  receipt_paragraph: "",
  possession_date: "",
  possession_includes_parking: false,
  has_annexure_a: false,
  signatory_enabled: false,
  signatory_company: "",
  signatory_name: "",
  signatory_email: "",
  applicant_layout: "standard",
  acknowledgement_show_date: true,
};

const VARIABLES_HINT = "Available variables: {{unitNo}}, {{areaDifference}}, {{areaDifferenceInSqFt}}, {{floorNo}}, {{wingName}}, {{bsp}}, {{paidAmt}}, {{paidDate}}, {{paidChequeNo}}, {{parkingfloor}}, {{parkingNumber}}, {{parkingLength}}, {{parkingBreadth}}, {{parkingHeight}}";

export default function AllotmentLetterAdmin() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // null = list view, object = form view
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadTemplates();
  }, []);

  async function loadTemplates() {
    const data = await getAllTemplates();
    setTemplates(data);
  }

  function startCreate() {
    setEditing({ ...EMPTY_TEMPLATE });
    setError("");
    setSuccess("");
  }

  function startEdit(tmpl) {
    setEditing({ ...tmpl, wing_id: tmpl.wing_id ?? "" });
    setError("");
    setSuccess("");
  }

  function cancelEdit() {
    setEditing(null);
    setError("");
    setSuccess("");
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setEditing((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!editing.project_id.trim()) {
      setError("Project ID is required.");
      return;
    }
    if (!editing.name.trim()) {
      setError("Template name is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...editing,
        wing_id: editing.wing_id?.trim() || null,
      };
      await saveTemplate(payload);
      setSuccess("Template saved successfully.");
      await loadTemplates();
      setEditing(null);
    } catch (err) {
      setError("Save failed: " + (err.message || JSON.stringify(err)));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id, name) {
    if (!window.confirm(`Delete template "${name}"? This cannot be undone.`)) return;
    try {
      await deleteTemplate(id);
      await loadTemplates();
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  }

  if (editing) {
    return <TemplateForm
      template={editing}
      onChange={handleChange}
      onSave={handleSave}
      onCancel={cancelEdit}
      saving={saving}
      error={error}
      success={success}
    />;
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h2 style={styles.title}>Allotment Letter Templates</h2>
        <button style={styles.primaryBtn} onClick={startCreate}>+ New Template</button>
      </div>

      {templates.length === 0 ? (
        <p style={styles.empty}>No templates yet. Click "New Template" to create one.</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Project ID</th>
              <th style={styles.th}>Wing ID</th>
              <th style={styles.th}>Show When</th>
              <th style={styles.th}>Parking</th>
              <th style={styles.th}>Annexure A</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((tmpl) => (
              <tr key={tmpl.id} style={styles.tr}>
                <td style={styles.td}>{tmpl.name}</td>
                <td style={{ ...styles.td, fontFamily: "monospace", fontSize: 11 }}>
                  {tmpl.project_id}
                </td>
                <td style={{ ...styles.td, fontFamily: "monospace", fontSize: 11 }}>
                  {tmpl.wing_id || "—"}
                </td>
                <td style={styles.td}>{tmpl.show_condition}</td>
                <td style={styles.td}>{tmpl.has_parking_section ? "Yes" : "No"}</td>
                <td style={styles.td}>{tmpl.has_annexure_a ? "Yes" : "No"}</td>
                <td style={styles.td}>
                  <button style={styles.editBtn} onClick={() => startEdit(tmpl)}>Edit</button>
                  <button style={styles.deleteBtn} onClick={() => handleDelete(tmpl.id, tmpl.name)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TemplateForm({ template, onChange, onSave, onCancel, saving, error, success }) {
  const isNew = !template.id;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h2 style={styles.title}>{isNew ? "New Template" : `Edit: ${template.name}`}</h2>
        <button style={styles.secondaryBtn} onClick={onCancel}>← Back to List</button>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}
      {success && <div style={styles.successBox}>{success}</div>}

      <form onSubmit={onSave}>
        <Section title="Identity">
          <Field label="Template Name *" hint="Used only in this dashboard">
            <input name="name" value={template.name} onChange={onChange} style={styles.input} required />
          </Field>
          <Field label="Project ID *" hint="UUID from the Projects table">
            <input name="project_id" value={template.project_id} onChange={onChange} style={styles.input} required />
          </Field>
          <Field label="Wing ID" hint="Leave blank for a project-level template. Fill only if this wing needs different content.">
            <input name="wing_id" value={template.wing_id || ""} onChange={onChange} style={styles.input} placeholder="(optional)" />
          </Field>
          <Field label="Show Allotment Letter Tab When" hint='"always" shows the tab for all units. Use the other options to hide it based on farvisionStatus.'>
            <select name="show_condition" value={template.show_condition} onChange={onChange} style={styles.input}>
              <option value="always">Always show</option>
              <option value="status:Available">Only when farvisionStatus = "Available"</option>
              <option value="not:HOLD">When farvisionStatus ≠ "HOLD"</option>
              <option value="not:Hold">When farvisionStatus ≠ "Hold"</option>
            </select>
          </Field>
        </Section>

        <Section title="Letter Content">
          <Field label="Subject Line *" hint="Full subject text — no variables needed">
            <textarea name="subject" value={template.subject} onChange={onChange} style={{ ...styles.input, ...styles.textarea }} required />
          </Field>
          <Field label="Section 1 — Allotment Paragraph" hint={VARIABLES_HINT}>
            <textarea name="section1_paragraph" value={template.section1_paragraph} onChange={onChange} style={{ ...styles.input, ...styles.textareaLg }} />
          </Field>
        </Section>

        <Section title="Parking">
          <Checkbox name="has_parking_section" checked={template.has_parking_section} onChange={onChange}
            label="Include Section 2 — Allotment of Garage / Covered Parking Space(s)"
            hint="When checked, parking details (parkingfloor, parkingNumber, etc.) are shown and all subsequent section numbers increase by 1." />
        </Section>

        <Section title="Receipt">
          <Field label="Receipt Paragraph" hint={`${VARIABLES_HINT}\nFor PNRD-style, write "Refer Cost Sheet" literally in the text instead of {{paidDate}} / {{paidChequeNo}}.`}>
            <textarea name="receipt_paragraph" value={template.receipt_paragraph} onChange={onChange} style={{ ...styles.input, ...styles.textareaLg }} />
          </Field>
        </Section>

        <Section title="Possession">
          <Field label="Possession Date *" hint='e.g. "31/12/2028"'>
            <input name="possession_date" value={template.possession_date} onChange={onChange} style={styles.input} required />
          </Field>
          <Checkbox name="possession_includes_parking" checked={template.possession_includes_parking} onChange={onChange}
            label='Add "along with covered car parking spaces(s)" to the possession clause' />
        </Section>

        <Section title="Annexure A">
          <Checkbox name="has_annexure_a" checked={template.has_annexure_a} onChange={onChange}
            label="Include Annexure A — Stage wise Schedule of Completion (20-row table on a new page)"
            hint="Required by RERA for most projects. PNRD does not have it." />
        </Section>

        <Section title="Signatory Block">
          <Checkbox name="signatory_enabled" checked={template.signatory_enabled} onChange={onChange}
            label="Show Authorized Signatory block" hint="PNRD (Wings A/B/C) does not show a signatory." />
          {template.signatory_enabled && (
            <>
              <Field label="Company (FOR …)">
                <input name="signatory_company" value={template.signatory_company || ""} onChange={onChange} style={styles.input}
                  placeholder="e.g. FOR VILAS JAVDEKAR ECO HOMES" />
              </Field>
              <Field label="Signatory Name">
                <input name="signatory_name" value={template.signatory_name || ""} onChange={onChange} style={styles.input}
                  placeholder="e.g. Mr. Vaibhav Kothamire" />
              </Field>
              <Field label="Signatory Email">
                <input name="signatory_email" value={template.signatory_email || ""} onChange={onChange} style={styles.input}
                  placeholder="e.g. vaibhav.kothamire@javdekars.com" />
              </Field>
            </>
          )}
        </Section>

        <Section title="Applicant Block Layout">
          <Field label="Layout" hint="Controls how applicant details appear at the top of the letter.">
            <select name="applicant_layout" value={template.applicant_layout} onChange={onChange} style={styles.input}>
              <option value="standard">Standard — shows co-applicant PAN + mobile (PPSBR, PKC)</option>
              <option value="pnrd">PNRD — co-applicant name only, no PAN/mobile (PNRD Wings A/B/C)</option>
              <option value="wingd">Wing D — Aadhaar + Email on right, no co-applicant block (PNRD Wing D)</option>
            </select>
          </Field>
          <Checkbox name="acknowledgement_show_date" checked={template.acknowledgement_show_date} onChange={onChange}
            label='Show "Date: ___" on the acknowledgement signature line'
            hint="PNRD (Wings A/B/C) does not show the date line." />
        </Section>

        <div style={styles.formFooter}>
          <button type="button" style={styles.secondaryBtn} onClick={onCancel}>Cancel</button>
          <button type="submit" style={styles.primaryBtn} disabled={saving}>
            {saving ? "Saving…" : "Save Template"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      {children}
      {hint && <p style={styles.hint}>{hint}</p>}
    </div>
  );
}

function Checkbox({ name, checked, onChange, label, hint }) {
  return (
    <div style={styles.field}>
      <label style={{ ...styles.label, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
        <input type="checkbox" name={name} checked={checked} onChange={onChange} />
        <span>{label}</span>
      </label>
      {hint && <p style={styles.hint}>{hint}</p>}
    </div>
  );
}

const styles = {
  page: { maxWidth: 900, margin: "0 auto", padding: "24px 16px", fontFamily: "sans-serif", fontSize: 14 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 },
  title: { margin: 0, fontSize: 22, fontWeight: 700 },
  empty: { color: "#666", textAlign: "center", padding: 40 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { padding: "8px 12px", background: "#f0f2f5", borderBottom: "2px solid #ddd", textAlign: "left", fontWeight: 600 },
  td: { padding: "8px 12px", borderBottom: "1px solid #eee", verticalAlign: "top" },
  tr: { cursor: "default" },
  section: { background: "#fafafa", border: "1px solid #e0e0e0", borderRadius: 8, padding: "20px 24px", marginBottom: 20 },
  sectionTitle: { margin: "0 0 16px", fontSize: 15, fontWeight: 600, color: "#333" },
  field: { marginBottom: 16 },
  label: { display: "block", fontWeight: 500, marginBottom: 4, color: "#333" },
  hint: { margin: "4px 0 0", fontSize: 12, color: "#666", whiteSpace: "pre-line" },
  input: { width: "100%", padding: "8px 10px", border: "1px solid #ccc", borderRadius: 6, fontSize: 13, boxSizing: "border-box" },
  textarea: { height: 80, resize: "vertical" },
  textareaLg: { height: 140, resize: "vertical" },
  formFooter: { display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 24, paddingTop: 16, borderTop: "1px solid #eee" },
  primaryBtn: { padding: "9px 20px", background: "#155EEF", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14 },
  secondaryBtn: { padding: "9px 20px", background: "#fff", color: "#344054", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", fontSize: 14 },
  editBtn: { marginRight: 8, padding: "4px 12px", background: "#e8f0fe", color: "#1a56db", border: "1px solid #c3d4f8", borderRadius: 4, cursor: "pointer", fontSize: 12 },
  deleteBtn: { padding: "4px 12px", background: "#fdecea", color: "#c0392b", border: "1px solid #f5c6c2", borderRadius: 4, cursor: "pointer", fontSize: 12 },
  errorBox: { background: "#fdecea", color: "#c0392b", padding: "10px 16px", borderRadius: 6, marginBottom: 16, border: "1px solid #f5c6c2" },
  successBox: { background: "#e6f9f0", color: "#1a7a4a", padding: "10px 16px", borderRadius: 6, marginBottom: 16, border: "1px solid #b2eacb" },
};
